import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import RoleProtectedRoute from "./Components/RoleProtectedRoute";
import { useAuth } from "./Context/Auth/AuthContext";
import { Box, CircularProgress } from "@mui/material";

// Eagerly load immediately required components
import MainIndex from "./Main/MainIndex";

// Lazy-loaded components
const AuthTabs = lazy(() => import("./Pages/AuthTabs"));
const Index = lazy(() => import("./Admin/Index"));
const Deposit = lazy(() => import("./Main/Layout/Deposit/Deposit"));
const Withdraw = lazy(() => import("./Main/Layout/Withdraw/Withdraw"));
const Wallet = lazy(() => import("./Main/Layout/Wallet/Wallet"));
const Treks = lazy(() => import("./Main/Layout/Treks/Treks"));
const Travel = lazy(() => import("./Main/Layout/Treks/Travel"));

const Routes = () => {
   const { userState } = useAuth();

   // 🔁 Preload user route components after login
   useEffect(() => {
      if (userState.isAuthenticated && userState.user?.role === "user") {
         import("./Main/Layout/Deposit/Deposit");
         import("./Main/Layout/Withdraw/Withdraw");
         import("./Main/Layout/Wallet/Wallet");
         import("./Main/Layout/Treks/Treks");
         import("./Main/Layout/Treks/Travel");
      }
   }, [userState]);

   const getRedirectPath = () => {
      if (!userState.isAuthenticated) return "/";
      switch (userState.user?.role) {
         case "admin":
            return "/admin";
         case "manager":
            return "/manager";
         case "user":
         default:
            return "/main";
      }
   };

   const baseRoutes = [
      {
         path: "/",
         element: userState.isAuthenticated ? <Navigate to={getRedirectPath()} /> : <AuthTabs />,
      },
   ];

   const userRoutes =
      userState.user?.role === "user"
         ? [
              {
                 path: "/main",
                 element: (
                    <RoleProtectedRoute allowedRoles={["user"]}>
                       <MainIndex />
                    </RoleProtectedRoute>
                 ),
                 children: [
                    { path: "deposit", element: <Deposit /> },
                    { path: "withdraw", element: <Withdraw /> },
                    { path: "wallet", element: <Wallet /> },
                    {
                       path: "treks",
                       element: <Treks />,
                       children: [{ path: "travel", element: <Travel /> }],
                    },
                 ],
              },
           ]
         : [];

   const adminRoutes =
      userState.user?.role === "admin"
         ? [
              {
                 path: "/admin",
                 element: (
                    <RoleProtectedRoute allowedRoles={["admin"]}>
                       <Index />
                    </RoleProtectedRoute>
                 ),
              },
           ]
         : [];

   const managerRoutes = ["admin", "manager"].includes(userState.user?.role)
      ? [
           {
              path: "/manager",
              element: (
                 <RoleProtectedRoute allowedRoles={["admin", "manager"]}>
                    <div>Manager Dashboard</div>
                 </RoleProtectedRoute>
              ),
           },
        ]
      : [];

   const fallbackRoute = [
      {
         path: "*",
         element: <Navigate to={getRedirectPath()} />,
      },
   ];

   const router = createBrowserRouter([
      ...baseRoutes,
      ...userRoutes,
      ...adminRoutes,
      ...managerRoutes,
      ...fallbackRoute,
   ]);

   return (
      <Suspense
         fallback={
            <Box
               sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
               }}
            >
               <CircularProgress size={40} thickness={4} />
            </Box>
         }
      >
         <RouterProvider router={router} />
      </Suspense>
   );
};

export default Routes;
